import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Counter from '../Counter';
import { BASE_CLASSES } from './constants';
import './index.scss';

/**
 * An expansion of the native <textarea>.
 * @property {String} classes This component's CSS classes.
 * @property {Boolean} controlled Whether this is a controlled component or not.
 * @see https://reactjs.org/docs/forms.html#controlled-components
 */
class Textarea extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            content: null,
            focused: false,
            element: null
        };

        this.handleChange = this.handleChange.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
    }

    get classes() {
        const {
            className,
            disabled,
            showCounter
        } = this.props;

        const { focused } = this.state;

        return classnames(BASE_CLASSES, className, {
            'fit-textarea-disabled': disabled,
            'fit-textarea-focused': focused,
            'fit-textarea-has-counter': showCounter
        });
    }

    get controlled() {
        return this.props.hasOwnProperty('value');
    }

    /**
     * Handles changes to the component's value, triggering the provided callback, and updating the state with the new value.
     * This ensures we have support for both:
     * "traditional" usage - with "children"
     * "React" usage - with "value"
     * @param {Event} e The change event.
     */
    handleChange(e) {
        const { onChange } = this.props;

        if (!this.controlled) {
            this.setState({
                content: e.target.value
            });
        }

        onChange && onChange(e);
    }

    /**
     * Handles the "onFocus" event, updating the state, and subsequently the UI.
     * @param {Event} e The change event.
     */
    onFocus(e) {
        const { onFocus } = this.props;

        this.setState({
            focused: true
        });

        onFocus && onFocus(e);
    }

    /**
     * Handles the "onBlur" event, updating the state, and subsequently the UI.
     * @param {Event} e The change event.
     */
    onBlur(e) {
        const { onBlur } = this.props;

        this.setState({
            focused: false
        });

        onBlur && onBlur(e);
    }

    /**
     * Truncates the component's value according to the "maxLength" passed.
     * @param  {String} content The component's value.
     * @return {String}         The trimmed value.
     */
    truncateContent(content = '') {
        const { maxLength } = this.props;

        if (typeof maxLength === 'undefined') {
            return content;
        }

        return content.substring(0, maxLength);
    }

    render() {
        const {
            children,
            value,
            disabled,
            maxLength,
            showCounter,
            ...rest
        } = this.props;

        const content = this.truncateContent(
            this.state.content ||
            children ||
            value ||
            ''
        );

        const props = {
            disabled,
            maxLength,
            ...rest
        };

        delete props.className; // We don't want to assign the passed class twice.

        if (!this.controlled) {
            props.defaultValue = props.defaultValue || content;
        } else {
            props.value = value;
        }

        return (
            <div className={this.classes}>
                <textarea {...props}
                    onChange={this.handleChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}/>
                {showCounter && <Counter className="fit-textarea-counter" current={content.length} max={maxLength}/>}
            </div>
        );
    }
}

Textarea.propTypes = {
    /**
     * Pre-rendered text.
     */
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * The textarea's value.
     * Passing this will make it a controlled component.
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * A callback function to be executed when the component's value changes.
     */
    onChange: PropTypes.func,
    /**
     * A callback function to be executed when the component gains focus.
     */
    onFocus: PropTypes.func,
    /**
     * A callback function to be executed when the component loses focus.
     */
    onBlur: PropTypes.func,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Whether or not the textarea is disabled.
     * A disabled textarea is not clickable, or editable.
     */
    disabled: PropTypes.bool,
    /**
     * The maximum length of the textarea.
     */
    maxLength: PropTypes.number,
    /**
     * Show a character counter.
     * If `maxLength` is also used, the counter will display the current character count out of the maximum allowed characters.
     */
    showCounter: PropTypes.bool
};

Textarea.defaultProps = {
    children: null,
    disabled: false
};

export default Textarea;
